import { APIConfig } from "./API";
import { PIN_CODE_KEY_OVERRIDE } from "./PinUnlockOverlayManager";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "./storage_utils";

export const productionApiConfig: APIConfig = {
  apiBaseUrl: "https://api.medflyt.com",
  socketIOServerUrl: "https://ws.medflyt.com",
};

export const stagingApiConfig: APIConfig = {
  apiBaseUrl: "https://medflyt-api-staging.herokuapp.com",
  socketIOServerUrl: "https://ws-stg.medflyt.com",
};

export const localApiConfig: APIConfig = {
  apiBaseUrl: "http://localhost:3000",
  socketIOServerUrl: "http://localhost:3005",
};

export const pApiConfig: APIConfig = {
  apiBaseUrl: "https://api-p.medflyt.com",
  socketIOServerUrl: "https://ws-p.medflyt.com",
};

export const API_CONFIG_KEY: string = "medflyt_app_api_config";

const API_BOOKMARKS_KEY: string = "medflyt_app_api_bookmarks";

const PASSTHROUGH_PARAM = "PT";
export const TRAINING_CENTER_PASSTHROUGH_BUNDLE_ITEM = "TCBIPass";
export const TRAINING_CENTER_PASSTHROUGH_BUNDLE_DUE_DATE = "TCBDDPass";

export interface APIConfigBookmark {
  readonly title: string;
  readonly apiConfig: APIConfig;
}

export async function getAPIBookmarks(): Promise<APIConfigBookmark[]> {
  const data = await getLocalStorageItem(API_BOOKMARKS_KEY);
  if (data === null) {
    return [];
  }
  const apiBookmarks = JSON.parse(data);

  // to deal with outdated local storage api bookmarks
  for (const bookmark of apiBookmarks) {
    if (!("title" in bookmark && "apiConfig" in bookmark.hasOwnProperty)) {
      // eslint-disable-line no-prototype-builtins
      clearAPIBookmarks();
      return [];
    }
  }

  // TODO validation + versioning
  return apiBookmarks;
}

export async function addAPIBookmark(api: APIConfigBookmark): Promise<void> {
  const prev = await getAPIBookmarks();
  prev.push(api);
  await setLocalStorageItem(API_BOOKMARKS_KEY, JSON.stringify(prev));
}

export async function clearAPIBookmarks(): Promise<void> {
  await removeLocalStorageItem(API_BOOKMARKS_KEY);
}

export async function loadAPIConfig(): Promise<APIConfig> {
  const data = await getLocalStorageItem(API_CONFIG_KEY);
  if (data === null) {
    const overrideAPIConfig = loadQueryParamsAPIConfig();
    if (overrideAPIConfig !== null) {
      return overrideAPIConfig;
    } else {
      return productionApiConfig;
    }
  }
  const apiConfig: APIConfig = JSON.parse(data);
  if (<any>apiConfig.apiBaseUrl === undefined || <any>apiConfig.socketIOServerUrl === undefined) {
    return productionApiConfig;
  }

  // TODO validate + versioning
  return apiConfig;
}

export function getPassthroughTokenQueryParamOrNull(): string | null {
  const passthrough = getParameterByName(PASSTHROUGH_PARAM);

  return passthrough;
}

export function getPassthroughTokenOrUndefined(): string | undefined {
  const passthrough = getPassthroughTokenQueryParamOrNull();

  if (passthrough === null || passthrough === "") {
    return;
  }

  saveTrainingCenterPassthroughDataIfExists();

  // Save pin code override as FIRST_TIME to skip pin code insert on passthrough
  sessionStorage.setItem(PIN_CODE_KEY_OVERRIDE, "FIRST_TIME");

  // Remove passthrough from query after fetching.
  const newURL = window.location.href.split(PASSTHROUGH_PARAM)[0];
  window.history.pushState({}, document.title, newURL.substring(0, newURL.length - 1));

  return passthrough;
}

export function saveTrainingCenterPassthroughDataIfExists() {
  const DUE_DATE_PASSTHROUGH_PARAM = "TCBDD";
  const BUNDLE_ITEM_PASSTHROUGH_PARAM = "TCBI";
  const dueDatePassthrough = getParameterByName(DUE_DATE_PASSTHROUGH_PARAM);
  const bundleItemPassthrough = getParameterByName(BUNDLE_ITEM_PASSTHROUGH_PARAM);

  if (
    (dueDatePassthrough === null || dueDatePassthrough === "") &&
    (bundleItemPassthrough === null || bundleItemPassthrough === "")
  ) {
    return;
  }

  let newURL = "";
  if (dueDatePassthrough !== null && dueDatePassthrough !== "") {
    // Remove passthrough from query after fetching.
    newURL = window.location.href.split(DUE_DATE_PASSTHROUGH_PARAM)[0];
    newURL = newURL.substring(0, newURL.length - 1);

    // Save to storage
    sessionStorage.setItem(TRAINING_CENTER_PASSTHROUGH_BUNDLE_DUE_DATE, dueDatePassthrough);
  }

  if (bundleItemPassthrough !== null && bundleItemPassthrough !== "") {
    // Remove passthrough from query after fetching.
    newURL = window.location.href.split(BUNDLE_ITEM_PASSTHROUGH_PARAM)[0];
    newURL = newURL.substring(0, newURL.length - 1);

    // Save to storage
    sessionStorage.setItem(TRAINING_CENTER_PASSTHROUGH_BUNDLE_ITEM, bundleItemPassthrough);
  }

  if (newURL !== "") {
    window.history.pushState({}, document.title, newURL);
  }
}

export async function saveAPIConfig(apiConfig: APIConfig): Promise<void> {
  await setLocalStorageItem(API_CONFIG_KEY, JSON.stringify(apiConfig));
}

function loadQueryParamsAPIConfig(): APIConfig | null {
  const overrideApiUrlParam = getParameterByName("overrideApiUrl");
  const overrideSocketUrlParam = getParameterByName("overrideSocketUrl");

  if (overrideApiUrlParam !== null && overrideSocketUrlParam !== null) {
    return {
      apiBaseUrl: overrideApiUrlParam,
      socketIOServerUrl: overrideSocketUrlParam,
    };
  } else {
    return null;
  }
}

// From: <https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript/901144#901144>
function getParameterByName(name: string, url?: string): string | null {
  const nameR = name.replace(/[[\]]/g, "\\$&");
  const regex = new RegExp("[?&]" + nameR + "(=([^&#]*)|&|#|$)");
  const results = regex.exec(url !== undefined ? url : window.location.href);
  if (results === null) {
    return null;
  }
  if (!(results[2] as unknown as boolean)) {
    return "";
  }
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}
